import { useState, useEffect, useMemo } from "react";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { formatCurrency, formatUSD, getUsdRate } from "@vaulzo/core/constants";
import AssetDialog from "./AssetDialog";
import USStocksModal from "./us-stocks/USStocksModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import TravelPointsDialog from "./TravelPointsDialog";
import GroupHeader from "./GroupHeader";
import { GROUPS, groupTotals, investmentsPutIn } from "@vaulzo/core/domain/groups";
import { groupRows, travelRows as buildTravelRows, categoriesForType, dialogTypeForGroup } from "@vaulzo/core/domain/assetRows";
import { useExchangeRates, useCryptoPrices, useSnapshotGroups } from "@vaulzo/core/hooks/usePortfolioQueries";
import { useSaveAsset, useDeleteAsset } from "@vaulzo/core/hooks/usePortfolioMutations";

const usd2 = (v) => formatUSD(v, { decimals: 2, suffix: "" });
// Older saved tabs map onto the group keys.
const LEGACY_TAB = { liquid: "investments", fixed: "property" };

/**
 * Assets page: one tab per group (Investments, Cash, Personal items,
 * Travel points, and Property once there is any), each with its own
 * total, history chart and rows. Owns all of its dialog state and
 * persists through the portfolio mutation hooks; the parent provides
 * the asset list and the cost-basis summary.
 */
export default function Assets({ assets, invested = null, initialSubTab = null }) {
  const { data: exchangeRates, isFetching: isLoadingRates } = useExchangeRates();
  const { data: cryptoPrices = [] } = useCryptoPrices();
  const saveAsset = useSaveAsset();
  const deleteAsset = useDeleteAsset();
  // A sub-tab handed over from the overview wins over the remembered one.
  const [activeSubTab, setActiveSubTab] = useState(() => {
    const saved = initialSubTab || localStorage.getItem("assetsActiveSubTab") || "investments";
    return LEGACY_TAB[saved] || saved;
  });
  const [dateRange, setDateRange] = useState("1m");
  const groupsQuery = useSnapshotGroups(dateRange);

  useEffect(() => {
    localStorage.setItem("assetsActiveSubTab", activeSubTab);
  }, [activeSubTab]);

  // Dialog state, colocated with the page that renders the dialogs.
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showTravelPointsDialog, setShowTravelPointsDialog] = useState(false);
  const [editingTravelPoints, setEditingTravelPoints] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // asset awaiting confirmation
  // US-stocks portfolio modal: { asset, tab, newEntry } while open.
  const [usModal, setUsModal] = useState(null);

  const isSaving = saveAsset.isPending;
  const usdRate = getUsdRate(exchangeRates);

  // Persist a payload, closing the dialog only on success so the user's
  // input survives a failed save.
  const saveWith = async (payload, id, onSuccess) => {
    try {
      await saveAsset.mutateAsync({ payload, id });
      onSuccess();
    } catch {
      /* error toast raised by the mutation hook */
    }
  };

  const handleAddSubmit = (payload) => saveWith(payload, undefined, () => setShowAddDialog(false));

  const handleEditSubmit = (payload) =>
    saveWith(payload, editingAsset.id, () => {
      setShowEditDialog(false);
      setEditingAsset(null);
    });

  const confirmDelete = async () => {
    const id = pendingDelete?.id;
    if (!id) return;
    setDeletingId(id);
    try {
      await deleteAsset.mutateAsync(id);
      setPendingDelete(null);
    } catch {
      /* error toast raised by the mutation hook */
    } finally {
      setDeletingId(null);
    }
  };

  // "Add asset → US Stocks" lands in the US-stocks modal: the existing
  // portfolio if there is one, otherwise a new, empty one.
  const openUsStocks = async () => {
    const existing = assets.find((a) => a.asset_type === "us_stocks" && a.ledger);
    if (existing) {
      setShowAddDialog(false);
      setUsModal({ asset: existing, tab: "activity", newEntry: true });
      return;
    }
    try {
      const created = await saveAsset.mutateAsync({
        payload: { name: "US Stocks", category: "US Stocks", asset_type: "us_stocks", ledger: true, current_value: 0 },
      });
      setShowAddDialog(false);
      setUsModal({ asset: created, tab: "activity", newEntry: true });
    } catch {
      /* error toast raised by the mutation hook */
    }
  };

  const openEdit = (asset) => {
    if (asset.asset_type === "us_stocks" && asset.ledger) {
      setUsModal({ asset, tab: "portfolio", newEntry: false });
      return;
    }
    setEditingAsset(asset);
    setShowEditDialog(true);
  };

  const handleEditTravelPoints = (asset) => {
    setEditingTravelPoints(asset);
    setShowTravelPointsDialog(true);
  };

  const closeTravelDialog = () => {
    setShowTravelPointsDialog(false);
    setEditingTravelPoints(null);
  };

  // Property only shows once there is something in it.
  const totals = useMemo(() => groupTotals(assets), [assets]);
  const tabs = GROUPS.filter((g) => g.key !== "property" || totals.property > 0);
  const group = tabs.some((t) => t.key === activeSubTab) ? activeSubTab : "investments";
  const isTravel = group === "travel";
  const groupTotal = totals[group] || 0;

  // Rows for the active group, ranked by value (built in core, shared with mobile).
  const rows = useMemo(() => (isTravel ? [] : groupRows(assets, group, cryptoPrices)), [assets, group, isTravel, cryptoPrices]);
  const rowsTotal = rows.reduce((s, r) => s + r.value, 0);
  const rowsMax = rows.length ? rows[0].value : 1;

  // Travel: one ranked row per loyalty program
  const travelRows = isTravel ? buildTravelRows(assets, usdRate) : [];
  const travelMax = travelRows.length ? travelRows[0].inr : 1;
  const travelInrTotal = travelRows.reduce((s, r) => s + r.inr, 0);
  const travelPtsTotal = travelRows.reduce((s, r) => s + r.pts, 0);

  const addLabel = isTravel ? "Add travel points" : "Add asset";
  const addBtnClass =
    "flex items-center gap-[7px] rounded-[10px] bg-vaulzo-accent px-[18px] py-[11px] text-sm font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover";
  const dialogType = dialogTypeForGroup(group);

  return (
    <main className="mx-auto max-w-[800px] px-6 pt-14">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em] text-vaulzo-ink">Assets</h1>
        <button
          onClick={() => (isTravel ? setShowTravelPointsDialog(true) : setShowAddDialog(true))}
          className={addBtnClass}
        >
          <span className="text-base leading-none">+</span>
          {addLabel}
        </button>
      </div>

      {/* Tabs — single line, horizontally scrollable on narrow screens.
          The hairline lives on the wrapper and the scroller overlaps it via
          its own -mb-px: buttons never overflow the scroller, so overflow-x
          can't spawn the 1px vertical scrollbar it otherwise would. */}
      <div className="mb-9 border-b border-vaulzo-hairline">
        <div className="no-scrollbar -mb-px flex gap-5 overflow-x-auto sm:gap-[30px]">
          {tabs.map((t) => {
            const active = group === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveSubTab(t.key)}
                className={`whitespace-nowrap border-b-2 py-[14px] text-sm transition-colors ${
                  active
                    ? "border-vaulzo-accent font-semibold text-vaulzo-ink"
                    : "border-transparent font-normal text-vaulzo-faint hover:text-vaulzo-ink"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <GroupHeader
        group={group}
        total={isTravel ? travelInrTotal : rowsTotal || groupTotal}
        series={groupsQuery.data ?? []}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        isLoading={groupsQuery.isFetching}
        putInToday={group === "investments" ? investmentsPutIn(invested, assets) : null}
      />

      {isTravel ? (
        <>
          <div className="flex items-baseline justify-between px-1 pb-2 pt-5 text-[12px] uppercase tracking-[0.04em] text-vaulzo-fainter">
            <span className="whitespace-nowrap">
              {travelRows.length} program{travelRows.length === 1 ? "" : "s"} ·{" "}
              {travelPtsTotal.toLocaleString("en-US")} pts
            </span>
            <span>Value</span>
          </div>

          {travelRows.length === 0 ? (
            <div className="py-[72px] text-center text-[15px] text-vaulzo-subtle">Nothing here yet.</div>
          ) : (
            travelRows.map((r, i) => (
              <div key={i} className="border-t border-vaulzo-row px-1 pb-[14px] pt-[15px]">
                <div className="flex items-center gap-[14px]">
                  <div className="min-w-0">
                    <div className="text-[15px] text-vaulzo-ink">{r.name}</div>
                    {r.pts > 0 && (
                      <div className="num mt-[3px] whitespace-nowrap text-[12px] text-vaulzo-fainter">
                        {r.pts.toLocaleString("en-US")} pts · ${r.rate.toFixed(4)}/pt
                      </div>
                    )}
                  </div>
                  <span className="flex-1" />
                  <div className="text-right">
                    <div className="num text-[15px] font-semibold text-vaulzo-ink">{formatCurrency(r.inr)}</div>
                    <div className="num mt-[3px] text-[12px] text-vaulzo-fainter">{usd2(r.usd)}</div>
                  </div>
                  <span className="flex w-10 justify-end">
                    <IconButton onClick={() => handleEditTravelPoints(r.asset)} hover="accent" label="Edit">
                      <Pencil className="h-[15px] w-[15px]" strokeWidth={1.7} />
                    </IconButton>
                  </span>
                </div>
                <ShareBar width={travelMax ? (r.inr / travelMax) * 100 : 0} lead={i === 0} />
              </div>
            ))
          )}

          {travelRows.length > 0 && <TotalRow value={formatCurrency(travelInrTotal)} />}
        </>
      ) : (
        <>
          <div className="flex items-baseline justify-between px-1 pb-2 pt-5 text-[12px] uppercase tracking-[0.04em] text-vaulzo-fainter">
            <span>{group === "cash" ? "Account" : group === "personal" ? "Item" : "Holding"}</span>
            <span>Value</span>
          </div>

          {rows.length === 0 ? (
            <div className="py-[72px] text-center text-[15px] text-vaulzo-subtle">Nothing here yet.</div>
          ) : (
            rows.map((r, i) => {
              const pct = rowsTotal ? (r.value / rowsTotal) * 100 : 0;
              const deleting = deletingId === r.asset.id;
              return (
                <div key={r.key} className="border-t border-vaulzo-row px-1 pb-[15px] pt-4">
                  {/* Name + meta stack on the left so long names truncate
                      instead of being crushed by the chips (mobile fix). */}
                  <div className="flex items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] text-vaulzo-ink">{r.name}</span>
                      {r.meta && (
                        <span className="num mt-[3px] block truncate text-[12px] text-vaulzo-fainter">{r.meta}</span>
                      )}
                      <span className="mt-[6px] flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="whitespace-nowrap rounded-full bg-vaulzo-row px-[11px] py-[3px] text-[12px] text-vaulzo-muted">
                          {r.chip}
                        </span>
                        {r.gain != null && (
                          <span
                            className={`num whitespace-nowrap rounded-full px-[9px] py-[3px] text-[12px] font-semibold ${
                              r.gain >= 0 ? "bg-vaulzo-accent-tint text-vaulzo-accent" : "bg-vaulzo-danger-track/40 text-vaulzo-danger"
                            }`}
                          >
                            {r.gain >= 0 ? "↑" : "↓"} {Math.abs(r.gain).toFixed(2)}%
                          </span>
                        )}
                        <span className="num text-[12px] text-vaulzo-subtle">{pct.toFixed(1)}%</span>
                        {r.isLedger && (
                          <button
                            type="button"
                            onClick={() => setUsModal({ asset: r.asset, tab: "activity", newEntry: true })}
                            className="whitespace-nowrap rounded-full bg-vaulzo-accent-tint px-[9px] py-[3px] text-[12px] font-semibold text-vaulzo-accent transition-colors hover:bg-vaulzo-accent hover:text-vaulzo-on-fill"
                          >
                            + Entry
                          </button>
                        )}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="num block whitespace-nowrap text-[15px] font-semibold text-vaulzo-ink">
                        {formatCurrency(r.value)}
                      </span>
                      {r.sub && <span className="num mt-[3px] block whitespace-nowrap text-[12px] text-vaulzo-fainter">{r.sub}</span>}
                    </span>
                    <span className="flex w-14 justify-end gap-1">
                      <IconButton onClick={() => openEdit(r.asset)} hover="accent" label="Edit">
                        <Pencil className="h-[15px] w-[15px]" strokeWidth={1.7} />
                      </IconButton>
                      {r.canDelete && (
                        <IconButton
                          onClick={() => setPendingDelete(r.asset)}
                          hover="danger"
                          label="Delete"
                          disabled={deletingId !== null}
                          muted
                        >
                          {deleting ? (
                            <Loader2 className="h-[15px] w-[15px] animate-spin" aria-hidden />
                          ) : (
                            <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.7} />
                          )}
                        </IconButton>
                      )}
                    </span>
                  </div>
                  <ShareBar width={rowsMax ? (r.value / rowsMax) * 100 : 0} lead={i === 0} />
                </div>
              );
            })
          )}

          {rows.length > 0 && <TotalRow value={formatCurrency(rowsTotal)} />}
        </>
      )}

      <AssetDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={handleAddSubmit}
        assetType={isTravel ? "travel_points" : dialogType}
        categories={categoriesForType(dialogType)}
        exchangeRates={exchangeRates}
        isLoadingRates={isLoadingRates}
        cryptoPrices={cryptoPrices}
        isSubmitting={isSaving}
        onUsStocks={openUsStocks}
      />

      <AssetDialog
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setEditingAsset(null);
        }}
        onSubmit={handleEditSubmit}
        editingAsset={editingAsset}
        assetType={editingAsset?.asset_type || dialogType}
        categories={categoriesForType(dialogType)}
        exchangeRates={exchangeRates}
        isLoadingRates={isLoadingRates}
        cryptoPrices={cryptoPrices}
        isSubmitting={isSaving}
      />

      <USStocksModal
        open={!!usModal}
        asset={usModal?.asset}
        initialTab={usModal?.tab}
        startWithNewEntry={usModal?.newEntry}
        onClose={() => setUsModal(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete asset?"
        message={pendingDelete ? `"${pendingDelete.name}" and its value will be removed from your portfolio.` : ""}
        busy={deletingId !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />

      <TravelPointsDialog
        isOpen={showTravelPointsDialog}
        onClose={closeTravelDialog}
        onSubmit={(e) => saveWith(e.assetData, editingTravelPoints?.id, closeTravelDialog)}
        title={editingTravelPoints ? "Edit Travel Points" : "Add Travel Points"}
        editingAsset={editingTravelPoints}
        exchangeRates={exchangeRates}
        isLoadingRates={isLoadingRates}
        isSubmitting={isSaving}
      />
    </main>
  );
}

function ShareBar({ width, lead }) {
  return (
    <div className="mt-3 h-[3px] rounded-sm bg-vaulzo-row">
      <div
        className="h-[3px] rounded-sm transition-all duration-500 ease-out"
        style={{ width: `${width}%`, backgroundColor: lead ? "rgb(var(--vz-accent))" : "rgb(var(--vz-bar))" }}
      />
    </div>
  );
}

function TotalRow({ value }) {
  return (
    <div className="mt-2 flex items-baseline justify-between border-t-2 border-vaulzo-ink px-1 pt-[22px]">
      <span className="text-[15px] font-semibold text-vaulzo-ink">Total</span>
      <span className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-ink">{value}</span>
    </div>
  );
}

function IconButton({ children, onClick, hover, label, disabled, muted }) {
  const base = muted ? "text-vaulzo-bar" : "text-vaulzo-fainter";
  const hoverClass = hover === "danger" ? "hover:text-vaulzo-danger" : "hover:text-vaulzo-accent";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`flex items-center p-[6px] transition-colors disabled:opacity-50 ${base} ${hoverClass}`}
    >
      {children}
    </button>
  );
}
